import {
	AspectRatioContainer,
	BreakpointWrapper,
	Container,
	ContainerPadding,
	CTA,
	Image,
	ReferenceBadge,
	Richtext,
	styled,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { default as React, useEffect, useState } from 'react';
import { PortalContext } from '../../../context/PortalContext';
import { Environment } from '../../../enviroment';
import { Vehicle } from '@esportal/portal-service';
import { alertsType, AlertType } from '../alerts-type';
import { Bonus } from '@esportal/portal-service';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { formatOwnership } from '../../../tracking/trackingHelper';
import { Add } from '@volkswagen-onehub/icons-core';
import { actionRedirect } from '../../../helpers/common-helpers';
import { useDisclaimers } from '../../../context/disclaimers-context';
import { COLOR_TOKEN, isNewBrandDesign } from '../../../helpers/color-tokens';

const ContainerEntregaIdeal = styled.div<{
	alertType: AlertType;
	alertPosition: number;
	numberOfAlerts: number;
	vehicleInterior: string;
	windowWidth: number;
}>`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	cursor: pointer;
	flex-grow: 1;
	width: 100%;
	border-radius: 16px;
	overflow: hidden;
	box-shadow:
		0px 3px 15px rgba(0, 14, 38, 0.1),
		0px 2px 4px rgba(0, 14, 38, 0.2);

	${(props: { alertType: AlertType; vehicleInterior: any }) =>
		props.alertType === AlertType.EntregaIdeal &&
		`
			background: linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 100%);

			/* Capa con ambas imágenes */
			&::before {
				content: '';
				position: absolute;
				inset: 0;
				z-index: 0;

				/* OJO: primero la imagen de fondo, luego la del interior (para que la interior quede arriba) */
				background-image:
					url(${props.vehicleInterior || 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/entrega_ideal-background.jpg'}),
					url('https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/entrega_ideal-background.jpg');

				background-position: center;
				background-repeat: no-repeat;
				background-size: cover;
				background-blend-mode: normal;

				/* Pequeña transparencia para que ambas se vean */
				opacity: 0.95;
			}
		`}

	/* Asegura que el contenido se vea sobre las imágenes */
	> * {
		position: relative;
		z-index: 1;
	}

	${(props: { alertType: AlertType; numberOfAlerts: number; windowWidth: number }) => {
		if (props.alertType === AlertType.EntregaIdeal && props.numberOfAlerts === 1) {
			if (props.windowWidth < 560) {
				return `aspect-ratio: 4 / 3; height: auto;`;
			}

			if (props.windowWidth >= 560 && props.windowWidth < 960) {
				return `aspect-ratio: 4 / 3; height: auto;`;
			}

			if (props.windowWidth >= 960 && props.windowWidth < 1600) {
				return `aspect-ratio: 2 / 1; height: auto;`;
			}

			if (props.windowWidth >= 1600) {
				return `aspect-ratio: 5 / 2; height: auto;`;
			}
		}
		return '';
	}}
`;

const CardContainer = styled.div<{
	alertType: AlertType;
	alertPosition: number;
	numberOfAlerts: number;
	vehicleInterior: string;
	windowWidth: number;
}>`
	position: relative;
	display: flex;
	cursor: pointer;
	height: 100%;
	width: ${(props: { numberOfAlerts: number; alertPosition: number; windowWidth: number }) => {
		const { numberOfAlerts, alertPosition, windowWidth } = props;

		if (windowWidth < 960) {
			return '100% !important';
		}

		if ((numberOfAlerts > 2 && alertPosition === 0) || (numberOfAlerts < 2 && alertPosition === 0)) {
			return '90.8vw';
		}
		if ((numberOfAlerts === 2 && alertPosition === 0) || (numberOfAlerts > 2 && alertPosition === 1)) {
			return '49vw';
		}
		if ((numberOfAlerts === 2 && alertPosition === 1) || (numberOfAlerts > 2 && alertPosition === 2)) {
			return '37vw';
		}

		return '100%';
	}};

	border-radius: 16px;
	text-align: left;
	background: ${COLOR_TOKEN.background.tertiary};
	box-shadow: ${isNewBrandDesign()
		? 'none'
		: '0px 3px 15px 0px rgba(0, 14, 38, 0.1), 0px 2px 4px 0px rgba(0, 14, 38, 0.2);'};

	flex-direction: ${(props: { windowWidth: number; numberOfAlerts: number; alertPosition: number }) =>
		(props.windowWidth >= 960 && props.numberOfAlerts > 2 && props.alertPosition === 0) ||
		(props.windowWidth >= 960 && props.numberOfAlerts === 1)
			? 'row'
			: 'column'};
`;

const ImageWrapper = styled.div`
	width: 58vw;
	min-width: 58vw;
	max-width: 58vw;
	border-radius: 16px 0 0 16px;
	overflow: hidden;
	flex-shrink: 0;
	height: 100%;
	display: flex;
	flex-direction: column;
`;

const TextWrapper = styled.div`
	padding: var(--size-dynamic0100);
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	height: 100%;
`;

const CTAContainer = styled.div<{
	numberOfAlerts: number;
	alertPosition: number;
}>`
	padding-bottom: var(--size-dynamic0100);
	padding-right: var(--size-dynamic0100);
`;

const CTAStyled = styled.div<{ alertType: AlertType }>`
	& > button {
		height: 40px;
		width: 40px;
		min-height: 40px;
		min-width: 40px;

		@media (min-width: 560px) and (max-width: 959px) {
			height: 48px;
			width: 48px;
			min-height: 48px;
			min-width: 48px;
		}

		@media (min-width: 960px) {
			height: 56px;
			width: 56px;
			min-height: 56px;
			min-width: 56px;
		}

		& svg {
			height: 20px;
			width: 20px;

			@media (min-width: 560px) and (max-width: 959px) {
				height: 24px;
				width: 24px;
			}

			@media (min-width: 960px) {
				height: 28px;
				width: 28px;
			}
		}
	}
`;

const DescriptionTextContainer = styled.div`
	@media screen and (max-width: 959px) {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: normal;
	}
`;

const StyledReferenceBadge = styled(ReferenceBadge)`
	vertical-align: top;
	&::before {
		content: none;
	}
`;

const AspectRatioWrapper = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;

	> * {
		flex-grow: 1;
		height: 100% !important;
	}
`;

const VideoStyled = styled.video`
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: 16px;
`;

export const AlertCard = ({
	vehicle,
	alertPosition,
	alertType,
	numberOfAlerts,
	bonus,
	setIsIdWeChargePopUpVisible,
	setIsPromotionPopupVisible,
	setAlertTypePromotion,
}: {
	vehicle: Vehicle | undefined;
	alertPosition: number;
	numberOfAlerts: number;
	alertType: AlertType;
	className: string;
	bonus: Bonus | null;
	setIsIdWeChargePopUpVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setIsPromotionPopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setAlertTypePromotion: (type: AlertType) => void;
}): React.ReactElement => {
	const { navigationService, userInteractionService } = React.useContext(PortalContext);

	const trackingManager = useTrackingManager();

	const handleClickAlert = async (vehicle: Vehicle | undefined, alertType: AlertType) => {
		trackingManager.trackClubVWButtonClick(
			'Alerts',
			{ contentId: `Alert ${alertType}` },
			{ vehicle: formatOwnership(vehicle ? [vehicle] : []) },
		);

		if (alertType === AlertType.Mantenimiento && vehicle) {
			const environmentPath = Environment.getInstance().maintenancePage as string;
			const search = window.location.search;
			const target = navigationService.navigateTo(environmentPath, {});

			if (target) {
				target.push();
				const href = target.createHref();
				addParamToUrl(href, vehicle.frame, search);
			}
		}

		if (alertType === AlertType.PreITV && vehicle) {
			actionRedirect(userInteractionService, 'action:es-cca-loader--open_layer');
		}

		if (alertType === AlertType.EntregaIdeal && bonus) {
			window.location.href = bonus.cta.bonusPageId;
		}

		if (alertType === AlertType.OneToOneID && bonus) {
			window.open(bonus.cta.bonusPageId, '_blank');
		}

		if (alertType === AlertType.OneToOnePolo && bonus) {
			window.open(bonus.cta.bonusPageId, '_blank');
		}

		if (alertType === AlertType.Touran) {
			window.open('https://www.volkswagen.es/es/modelos/touran.html#ofertas');
		}

		if (alertType === AlertType.GolfGTI) {
			const environmentPath = Environment.getInstance().gti50 as string;
			const target = navigationService.navigateTo(environmentPath, {});
			if (target) {
				target.push();
				const currentUrl = window.location.href;
				const quizzUrl = currentUrl + '/__app/quiz.app';
				window.location.href = quizzUrl;
			} else {
				console.log('detail page not created');
			}
		}

		if (alertType === AlertType.IDWeCharge) {
			setIsIdWeChargePopUpVisible(true);
		}

		if (alertType === AlertType.IDWeChargePostPedido) {
			const environmentPath = Environment.getInstance().bonusDetailPageId as string;
			const search = window.location.search;
			const target = navigationService.navigateTo(environmentPath, {});
			if (target && bonus) {
				target.push();
				const href = target.createHref();
				const newSearchParams = new URLSearchParams(search);
				newSearchParams.append('bonus-id', bonus?.id);
				window.history.replaceState(null, '', href + `?${newSearchParams.toString()}`);
			} else {
				console.log('detail page not created');
			}
		}

		if (
			alertType === AlertType.Masters26_Open ||
			alertType === AlertType.Masters26_Enrolled ||
			alertType === AlertType.Masters26_NoWinner
		) {
			const environmentPath = Environment.getInstance().mastersCarretera as string;
			const target = navigationService.navigateTo(environmentPath, {});
			if (target) {
				target.push();
			}
		}
		if (alertType === AlertType.Masters26_Winner) {
			setAlertTypePromotion(AlertType.Masters26_Winner);
			setIsPromotionPopupVisible(true);
		}

		if (alertType === AlertType.LaLiga26_Open) {
			setAlertTypePromotion(AlertType.LaLiga26_Open);
			setIsPromotionPopupVisible(true);
		}

		if (alertType === AlertType.LaLiga26_Enrolled || alertType === AlertType.LaLiga26_NoWinner) {
			const environmentPath = Environment.getInstance().laLiga as string;
			const target = navigationService.navigateTo(environmentPath, {});
			if (target) {
				target.push();
			}
		}

		if (alertType === AlertType.ElClasico26_Open) {
			setAlertTypePromotion(AlertType.ElClasico26_Open);
			setIsPromotionPopupVisible(true);
		}

		if (alertType === AlertType.ElClasico26_Enrolled || alertType === AlertType.ElClasico26_NoWinner) {
			const environmentPath = Environment.getInstance().laLiga as string;
			const target = navigationService.navigateTo(environmentPath, {});
			if (target) {
				target.push();
			}
		}

		// GTI Day 2026
		if (alertType === AlertType.GTIDay26_Open) {
			setAlertTypePromotion(AlertType.GTIDay26_Open);
			setIsPromotionPopupVisible(true);
		}
		if (alertType === AlertType.GTIDay26_Enrolled) {
			window.open('https://gti50fest.com/', '_blank');
		}

		// GTI Day VIP 2026
		if (alertType === AlertType.GTIDayVIP26_Open) {
			setAlertTypePromotion(AlertType.GTIDayVIP26_Open);
			setIsPromotionPopupVisible(true);
		}
		if (alertType === AlertType.GTIDayVIP26_Enrolled || alertType === AlertType.GTIDayVIP26_Full) {
			window.open('https://gti50fest.com/', '_blank');
		}
	};

	const addParamToUrl = (href: string, param: string, search: string) => {
		const newSearchParams = new URLSearchParams(search);
		newSearchParams.append('frame', param);
		window.history.replaceState(null, '', href + `?${newSearchParams.toString()}`);
	};

	const personalizeAlertTitle = (alertTitle: string): string => {
		if (alertType !== AlertType.Mantenimiento && alertType !== AlertType.PreITV) {
			return alertTitle;
		}

		if (!vehicle?.comercialDenomination) {
			return alertTitle;
		}

		return alertTitle.replace('[modelo]', vehicle.comercialDenomination);
	};

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const getImageSrcByAlertType = (alertType: AlertType, vehicle?: Vehicle): string => {
		if (alertType === AlertType.Mantenimiento && vehicle?.mediaAssets?.Front_Left) {
			return vehicle.mediaAssets.Front_Left;
		}
		switch (alertType) {
			case AlertType.Mantenimiento:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/mantenimiento.jpg';
			case AlertType.PreITV:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/alert_itv.jpg';
			case AlertType.EntregaIdeal:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/entrega_ideal-background.jpg';
			case AlertType.OneToOneID:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/alert-one2one-ID.jpg';
			case AlertType.OneToOnePolo:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/alert-one2one-GTI.jpg';
			case AlertType.Touran:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/alert_touran.jpg';
			case AlertType.GolfGTI:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/turismos_golf_gti_50_aniversario_alerta_destapado1?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJmE5NmU=';
			case AlertType.IDWeCharge:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_club_campana_id_wecharge_2560x1706px?Zml0PWNyb3AlMkMxJndpZD0yNTU5JmhlaT0xNzA2JmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjc2NmI=';
			case AlertType.IDWeChargePostPedido:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/camapana_id_alerta_1600x1200px?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.VWBook26_Participa:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-vwbook/gif_libro_1600x1200px.gif';
			case AlertType.VWBook26_Participado:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-vwbook/gif_libro_1600x1200px.gif';
			case AlertType.VWBook26_Agotado:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_layer_libro_agotado_1600x1200px?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.Masters26_Open:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-promotion-masters/Promotion-masters-alert.gif';
			case AlertType.Masters26_Enrolled:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta1?Zml0PWNyb3AlMkMxJndpZD0xMDAwJmhlaT00MDAmZm10PWpwZWcmcWx0PTc5JmJmYz1vZmYmMWQ3Mw==';
			case AlertType.Masters26_NoWinner:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/no_seleccionado?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.Masters26_Winner:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/seleccionado_fase4?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.LaLiga26_Open:
				return 'https://assets.volkswagen.com/is/content/volkswagenag/VW_Goles_6seg_A_Generico_LOGO-0x720-3000k';
			case AlertType.LaLiga26_Enrolled:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_laliga_3?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.LaLiga26_NoWinner:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/Alerta_LaLiga2?Zml0PWNyb3AlMkMxJndpZD0xNzcwJmhlaT0xMzI4JmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjNmMmI=';
			case AlertType.ElClasico26_Open:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/LaLiga_Clasico_Alerta_NEWBigComposite?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT04NTgmZm10PWpwZWcmcWx0PTc5JmJmYz1vZmYmNGRhNQ==';
			case AlertType.ElClasico26_Enrolled:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/LaLiga_Clasico_Alerta_NEWBigComposite?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT04NTgmZm10PWpwZWcmcWx0PTc5JmJmYz1vZmYmNGRhNQ==';
			case AlertType.ElClasico26_NoWinner:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/LaLiga_Clasico_Alerta_NEWBigComposite?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT04NTgmZm10PWpwZWcmcWx0PTc5JmJmYz1vZmYmNGRhNQ==';
			case AlertType.GTIDay26_Open:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_open_50_golf_gti?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1wbmctYWxwaGEmYmZjPW9mZiY4ZTMw';
			case AlertType.GTIDay26_Enrolled:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_enrolled_50_golf_gti?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.GTIDayVIP26_Open:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_open_50_golf_gti?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1wbmctYWxwaGEmYmZjPW9mZiY4ZTMw';
			case AlertType.GTIDayVIP26_Enrolled:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_enrolled_50_golf_gti?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmM3MWE=';
			case AlertType.GTIDayVIP26_Full:
				return 'https://assets.volkswagen.com/is/image/volkswagenag/alerta_open_50_golf_gti_Agotado?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1wbmctYWxwaGEmYmZjPW9mZiY4ZTMw';
			default:
				return 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/default-image.jpg';
		}
	};

	const renderAlertMedia = () => {
		const mediaSrc = getImageSrcByAlertType(alertType, vehicle);

		if (alertType === AlertType.LaLiga26_Open) {
			return <VideoStyled src={mediaSrc} autoPlay playsInline muted loop />;
		}

		return (
			<Image
				data-testid="image-alert"
				src={mediaSrc}
				objectFit="cover"
				alt={vehicle?.comercialDenomination || 'Alert image'}
				style={{ borderRadius: '16px' }}
			/>
		);
	};

	const currentAlertType = alertsType.find((type) => type.type === alertType);

	// Obtener el texto del disclaimer basado en el tipo de alerta
	const getAlertDisclaimerText = () => {
		if (alertType === AlertType.IDWeCharge || alertType === AlertType.IDWeChargePostPedido) {
			return 'Promoción aplicable a pedidos de clientes particulares de vehículos 100% eléctricos realizados entre el 01/10/2025 y el 31/12/2025, ambos inclusive. Al adquirir un modelo de la gama ID. de Volkswagen Turismos, los clientes particulares recibirán un bono de carga pública con un importe de 733€, que se podrá canjear únicamente en las estaciones de recarga que forman parte de WeCharge, el servicio de recarga disponible en la aplicación Volkswagen. Este bono de 733€ corresponde a una estimación orientativa del coste de 10.000 km de carga pública (calculado en base a un consumo WLTP homologado de 15,9 kWh/100 km para el modelo ID.4 Pro, recargando en la red IONITY a 0,37€/kWh mediante la tarifa WeCharge Plus; importe de 588,30€) más el importe equivalente a 12 mensualidades de dicha tarifa (11,99€/mes, total 143,88€). Esta estimación no es vinculante y puede variar según el uso real del vehículo, las condiciones de carga y las tarifas eléctricas aplicables. El bono puede utilizarse con cualquier suscripción activa de WeCharge. El cliente deberá abonar las mensualidades de la suscripción que elija, y el valor equivalente incluido en el bono podrá redimirse en forma de kWh de carga pública. El bono deberá activarse en un plazo máximo de tres meses desde la entrega del vehículo, y para ello el cliente deberá estar registrado en el Club Volkswagen a través del siguiente enlace: <a href="https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html" style="color:#3C484D;">https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html</a>. El importe del bono deberá consumirse antes del 31/12/2029. No canjeable por dinero en metálico.';
		}
		if (alertType === AlertType.VWBook26_Participa) {
			return 'Para poder obtener de forma gratuita una de las 400 unidades de la edición limitada del Libro de diseño Volkswagen es necesario ser miembro del Club Volkswagen y cumplimentar debidamente el correspondiente formulario de solicitud al cual podrás acceder a través del siguiente enlace <a href="https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html" style="color:#3C484D;">https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html</a>, debiendo seleccionar el Concesionario al que acudirás a recoger el Libro.Promoción válida hasta fin de existencias.';
		}
		return null;
	};

	const alertDisclaimerText = getAlertDisclaimerText();
	const { findBadge } = useDisclaimers();
	const badge = alertDisclaimerText ? findBadge(alertDisclaimerText) : undefined;

	const getAspectRatio = (numberOfAlerts: number, alertPosition: number, windowWidth: number) => {
		if (numberOfAlerts === 1) {
			if (windowWidth < 560) return '4:3';
			if (windowWidth < 1280) return '2:1';
			return '2:1';
		}

		if (numberOfAlerts > 2 && alertPosition === 0) {
			return windowWidth < 560 ? '4:3' : '2:1';
		}

		if ((numberOfAlerts > 2 && alertPosition === 1) || (numberOfAlerts === 2 && alertPosition === 0)) {
			return windowWidth < 960 ? '16:9' : '2:1';
		}

		if ((numberOfAlerts > 2 && alertPosition === 2) || (numberOfAlerts === 2 && alertPosition === 1)) {
			return windowWidth < 960 ? '2:1' : '3:2';
		}

		return '16:9';
	};

	return (
		<>
			{currentAlertType && (
				<>
					<CardContainer
						className="alert-card-container"
						alertType={alertType}
						alertPosition={alertPosition}
						numberOfAlerts={numberOfAlerts}
						vehicleInterior={vehicle?.mediaAssets?.Interior_Lhd_Center || vehicle?.mediaAssets?.Interior_Side || ''}
						onClick={() => handleClickAlert(vehicle, alertType)}
						windowWidth={windowWidth}
					>
						{(windowWidth >= 960 && numberOfAlerts > 2 && alertPosition === 0) ||
						(windowWidth >= 960 && numberOfAlerts === 1) ? (
							<>
								{alertType === AlertType.EntregaIdeal ? (
									<ContainerEntregaIdeal
										data-testid="entrega-ideal"
										alertType={alertType}
										alertPosition={alertPosition}
										numberOfAlerts={numberOfAlerts}
										windowWidth={windowWidth}
										vehicleInterior={
											vehicle?.mediaAssets?.Interior_Lhd_Center ?? vehicle?.mediaAssets?.Interior_Side ?? ''
										}
										style={{ borderRadius: '16px' }}
									/>
								) : (
									<ImageWrapper>
										<AspectRatioWrapper>
											<AspectRatioContainer ratio={getAspectRatio(numberOfAlerts, alertPosition, windowWidth)}>
												{renderAlertMedia()}
											</AspectRatioContainer>
										</AspectRatioWrapper>
									</ImageWrapper>
								)}

								<ContentWrapper wrap="always">
									<TextWrapper>
										<Container wrap="always">
											<BreakpointWrapper>
												<Container padding={{ bottom: ContainerPadding.static150 }} data-testid="alert-card">
													<Text appearance={TokenTextAppearance.headline300}>
														<Richtext
															dangerouslyParseRawHTML
															markdown={personalizeAlertTitle(currentAlertType?.title)}
														/>
													</Text>
												</Container>

												<Text appearance={TokenTextAppearance.copy200}>
													<DescriptionTextContainer className="description-text-container">
														<Richtext dangerouslyParseRawHTML markdown={currentAlertType?.description} />
														{badge && (
															<>
																{' '}
																<StyledReferenceBadge>{badge.replace(/\.$/, '')}</StyledReferenceBadge>
															</>
														)}
													</DescriptionTextContainer>
												</Text>
											</BreakpointWrapper>
										</Container>
									</TextWrapper>
									{alertType !== AlertType.VWBook26_Agotado && alertType !== AlertType.VWBook26_Participado && (
										<CTAContainer numberOfAlerts={numberOfAlerts} alertPosition={alertPosition}>
											<Container horizontalAlign={'flex-end'} stretchContent>
												<Text textAlign={TextAlignment.end}>
													<CTAStyled className="cta-styled" alertType={alertType}>
														<CTA tag="button" emphasis={'nbab'} icon={<Add variant="default" aria-hidden />} />
													</CTAStyled>
												</Text>
											</Container>
										</CTAContainer>
									)}
								</ContentWrapper>
							</>
						) : (
							<>
								<ContentWrapper>
									{alertType === AlertType.EntregaIdeal ? (
										<ContainerEntregaIdeal
											data-testid="entrega-ideal"
											alertType={alertType}
											alertPosition={alertPosition}
											numberOfAlerts={numberOfAlerts}
											windowWidth={windowWidth}
											vehicleInterior={
												vehicle?.mediaAssets?.Interior_Lhd_Center ?? vehicle?.mediaAssets?.Interior_Side ?? ''
											}
											style={{ borderRadius: '16px' }}
										/>
									) : (
										<AspectRatioContainer ratio={getAspectRatio(numberOfAlerts, alertPosition, windowWidth)}>
											{renderAlertMedia()}
										</AspectRatioContainer>
									)}

									<Container wrap="always" padding={ContainerPadding.dynamic0100}>
										<BreakpointWrapper>
											<Container padding={{ bottom: ContainerPadding.static150 }} data-testid="alert-card">
												<Text appearance={TokenTextAppearance.headline300}>
													<Richtext dangerouslyParseRawHTML markdown={personalizeAlertTitle(currentAlertType?.title)} />
												</Text>
											</Container>

											<Text appearance={TokenTextAppearance.copy200}>
												<DescriptionTextContainer className="description-text-container">
													<Richtext dangerouslyParseRawHTML markdown={currentAlertType?.description} />{' '}
													{badge && <StyledReferenceBadge>{badge.replace(/\.$/, '')}</StyledReferenceBadge>}
												</DescriptionTextContainer>
											</Text>
										</BreakpointWrapper>
									</Container>
								</ContentWrapper>
								{alertType !== AlertType.VWBook26_Agotado && alertType !== AlertType.VWBook26_Participado && (
									<CTAContainer numberOfAlerts={numberOfAlerts} alertPosition={alertPosition}>
										<Container horizontalAlign={'flex-end'} stretchContent>
											<Text textAlign={TextAlignment.end}>
												<CTAStyled className="cta-styled" alertType={alertType}>
													<CTA tag="button" emphasis={'nbab'} icon={<Add variant="default" aria-hidden />} />
												</CTAStyled>
											</Text>
										</Container>
									</CTAContainer>
								)}
							</>
						)}
					</CardContainer>
				</>
			)}
		</>
	);
};
